/**
 * Iter209an — Rich text editor riusabile basato su react-quill-new
 * (compat React 19). Espone value/onChange come una qualsiasi Input
 * shadcn e supporta le funzionalità richieste:
 *   - grassetto, corsivo, sottolineato, barrato
 *   - font family (Arial, Georgia, Verdana, Times New Roman, Trebuchet)
 *   - dimensione testo
 *   - colore testo + colore sfondo
 *   - allineamenti (sinistra, centro, destra, giustificato)
 *   - liste puntate/numerate
 *   - link ipertestuali
 *   - inserimento immagini (URL o upload)
 *
 * Uso:
 *   <RichTextEditor value={html} onChange={setHtml}
 *                   uploadEndpoint="/api/uploads/rich-image" />
 */
import React, { useEffect, useMemo, useRef } from "react";
import ReactQuill, { Quill } from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { api } from "@/lib/api";
import { toast } from "sonner";

// -------------------- Font whitelist (Iter209an) --------------------
// Aggiungiamo tutte le opzioni supportate al registry di Quill così che
// vengano rese effettive dai comandi della toolbar.
const FONT_WHITELIST = [
  "arial", "georgia", "verdana", "times-new-roman", "trebuchet",
];
const SIZE_WHITELIST = ["10px", "12px", "14px", "16px", "18px", "24px", "32px"];

// Registrazione one-shot lato modulo (idempotente).
try {
  const Font = Quill.import("formats/font");
  Font.whitelist = FONT_WHITELIST;
  Quill.register(Font, true);
  const Size = Quill.import("attributors/style/size");
  Size.whitelist = SIZE_WHITELIST;
  Quill.register(Size, true);
} catch {
  /* già registrato o import fallito silenziosamente */
}

const FONT_LABELS = {
  arial: "Arial",
  georgia: "Georgia",
  verdana: "Verdana",
  "times-new-roman": "Times New Roman",
  trebuchet: "Trebuchet MS",
};

export default function RichTextEditor({
  value = "",
  onChange,
  placeholder = "Scrivi qui…",
  minHeight = 180,
  uploadEndpoint = null, // se null → chiede URL con prompt
  testId = "rich-text-editor",
}) {
  const quillRef = useRef(null);

  // Handler personalizzato "image": prova upload se `uploadEndpoint`
  // è fornito, altrimenti chiede URL con prompt.
  const imageHandler = () => {
    if (!uploadEndpoint) {
      const url = window.prompt("Inserisci URL immagine (https://…):");
      if (!url) return;
      const editor = quillRef.current?.getEditor();
      const range = editor?.getSelection(true);
      editor?.insertEmbed(range?.index ?? 0, "image", url, "user");
      return;
    }
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const fd = new FormData();
      fd.append("file", file);
      try {
        const r = await api.post(uploadEndpoint, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const url = r.data?.url || r.data?.signed_url;
        if (!url) throw new Error("URL immagine mancante nella risposta");
        const editor = quillRef.current?.getEditor();
        const range = editor?.getSelection(true);
        editor?.insertEmbed(range?.index ?? 0, "image", url, "user");
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Upload immagine fallito");
      }
    };
    input.click();
  };

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ font: FONT_WHITELIST }, { size: SIZE_WHITELIST }],
        ["bold", "italic", "underline", "strike"],
        [{ color: [] }, { background: [] }],
        [{ align: "" }, { align: "center" }, { align: "right" }, { align: "justify" }],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link", "image"],
        ["clean"],
      ],
      handlers: { image: imageHandler },
    },
    clipboard: { matchVisual: false },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [uploadEndpoint]);

  const formats = [
    "font", "size",
    "bold", "italic", "underline", "strike",
    "color", "background",
    "align",
    "list",
    "link", "image",
  ];

  // Applica il font "arial" come default via CSS embed
  useEffect(() => {
    const styleId = "rich-text-editor-fonts";
    if (document.getElementById(styleId)) return;
    const style = document.createElement("style");
    style.id = styleId;
    style.innerHTML = `
      .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="arial"]::before,
      .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="arial"]::before { content: 'Arial'; font-family: Arial, sans-serif; }
      .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="georgia"]::before,
      .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="georgia"]::before { content: 'Georgia'; font-family: Georgia, serif; }
      .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="verdana"]::before,
      .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="verdana"]::before { content: 'Verdana'; font-family: Verdana, sans-serif; }
      .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="times-new-roman"]::before,
      .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="times-new-roman"]::before { content: 'Times New Roman'; font-family: 'Times New Roman', serif; }
      .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="trebuchet"]::before,
      .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="trebuchet"]::before { content: 'Trebuchet MS'; font-family: 'Trebuchet MS', sans-serif; }
      .ql-font-arial { font-family: Arial, sans-serif; }
      .ql-font-georgia { font-family: Georgia, serif; }
      .ql-font-verdana { font-family: Verdana, sans-serif; }
      .ql-font-times-new-roman { font-family: 'Times New Roman', serif; }
      .ql-font-trebuchet { font-family: 'Trebuchet MS', sans-serif; }
      .ql-editor { min-height: ${minHeight}px; font-size: 14px; }
      .ql-editor img { max-width: 100%; height: auto; }
    `;
    document.head.appendChild(style);
  }, [minHeight]);

  return (
    <div className="rich-text-editor-wrapper" data-testid={testId}>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        modules={modules}
        formats={formats}
      />
    </div>
  );
}

export { FONT_LABELS, FONT_WHITELIST, SIZE_WHITELIST };
